import { supabase } from '../services/supabase';

/**
 * The claim funnel on Start (JT via Creator Brain, PROJECT 300; Nicky's OK,
 * 29 Oct 2026): each step of a claim visit is reported to page_claim_step,
 * keyed by the page (the bar's claim slug, or the onboarding link's token,
 * which the server resolves and never returns) and an anonymous per-tab
 * session id. Never a code, phone number, email or password: only the event
 * and a category, channel or step number from fixed lists (the server keeps
 * only those). Fire-and-forget: a report that fails changes nothing on screen.
 */

export type ClaimFunnelEvent =
  | 'claim_start_viewed'
  | 'claim_email_submitted'
  | 'claim_verify_sent'
  | 'claim_verify_failed'
  | 'claim_verify_ok'
  | 'claim_step_reached';

export type ClaimFailureCategory = 'wrong_code' | 'expired' | 'rate_limited' | 'delivery_error' | 'other';

export type ClaimFunnelDetail =
  | { category: 'new_user' | 'existing_user' }
  | { category: ClaimFailureCategory }
  | { channel: 'sms' }
  | { step: number };

/** Which page this Start visit is for: an onboarding link, else a claim slug. */
export type ClaimFunnelContext = { token: string } | { slug: string };

const SESSION_KEY = 'claim_funnel_session';
const CONTEXT_KEY = 'claim_funnel_context';

function tabStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function randomId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// The id for this page load when the tab's storage can't keep one (blocked or
// full): one visit still reads as one session, at least until a reload.
let memorySession: string | null = null;

/** One anonymous id per tab, kept across the email/code round-trip and reloads. */
export function claimFunnelSession(): string {
  if (memorySession) return memorySession;
  const store = tabStorage();
  try {
    const kept = store?.getItem(SESSION_KEY);
    if (kept && /^[A-Za-z0-9_-]{16,64}$/.test(kept)) return kept;
    const fresh = randomId();
    if (!store) {
      memorySession = fresh;
      return fresh;
    }
    store.setItem(SESSION_KEY, fresh);
    if (store.getItem(SESSION_KEY) !== fresh) memorySession = fresh;
    return fresh;
  } catch {
    memorySession = randomId();
    return memorySession;
  }
}

/** Tests only: forget the in-memory id. */
export function resetClaimFunnelSessionForTests(): void {
  memorySession = null;
}

/**
 * The visit's page: the link or slug in the address, else the one this tab
 * already had (Start drops both from its own storage once the claim is made,
 * but the steps after it still belong to the same visit). An onboarding link
 * beats a claim slug, as it does on Start.
 */
export function claimFunnelContext(token: string | null | undefined, slug: string | null | undefined): ClaimFunnelContext | null {
  const store = tabStorage();
  const t = (token ?? '').trim();
  const s = (slug ?? '').trim().toLowerCase();
  let ctx: ClaimFunnelContext | null = null;
  if (t.length >= 8 && t.length <= 200) ctx = { token: t };
  else if (/^[a-z0-9._-]{1,64}$/.test(s)) ctx = { slug: s };
  try {
    if (ctx) {
      store?.setItem(CONTEXT_KEY, JSON.stringify(ctx));
      return ctx;
    }
    const kept = JSON.parse(store?.getItem(CONTEXT_KEY) || 'null');
    if (kept && typeof kept.token === 'string') return { token: kept.token };
    if (kept && typeof kept.slug === 'string') return { slug: kept.slug };
  } catch {
    // storage blocked or unreadable: this visit just isn't tracked
  }
  return ctx;
}

/** What went wrong, from the sign-in functions' own messages. */
export function claimFailureCategory(message: string | null | undefined): ClaimFailureCategory {
  const m = (message ?? '').toLowerCase();
  if (/invalid code/.test(m)) return 'wrong_code';
  if (/expired|already used/.test(m)) return 'expired';
  if (/wait 60 seconds|too many/.test(m)) return 'rate_limited';
  if (/couldn.t send|failed to send|can.t text|deliver/.test(m)) return 'delivery_error';
  return 'other';
}

/** Report one step. Does nothing outside a claim visit. */
export function trackClaimStep(
  ctx: ClaimFunnelContext | null,
  event: ClaimFunnelEvent,
  detail?: ClaimFunnelDetail
): void {
  if (!ctx) return;
  try {
    void Promise.resolve(
      supabase.rpc('page_claim_step', {
        p_session: claimFunnelSession(),
        p_event: event,
        p_slug: 'slug' in ctx ? ctx.slug : null,
        p_token: 'token' in ctx ? ctx.token : null,
        p_detail: detail ?? {},
      })
    ).catch(() => undefined);
  } catch {
    // never in the way of signing up
  }
}
